import React, { useMemo, useState } from 'react';
import { Tooltip, ClickAwayListener, TooltipProps } from '@mui/material';

type TriggerMode = 'click' | 'hover';

type LazyTitle =
    | React.ReactNode
    | (() => React.ReactNode);

type CustomTooltipProps = Omit<TooltipProps, 'title'> & {
    title: LazyTitle;
    lazyMountTitle?: boolean;
    trigger?: TriggerMode;
};

const CustomTooltip = ({
    children,
    title,
    lazyMountTitle = false,
    trigger = 'click',
    slotProps,
    onOpen,
    onClose,
    ...rest
}: CustomTooltipProps) => {
    const [ open, setOpen ] = useState(false);
    const [ hasOpened, setHasOpened ] = useState(false);

    const handleOpen = (event?: any) => {
        if (!hasOpened) {
            setHasOpened(true);
        }
        setOpen(true);
        onOpen?.(event);
    };

    const handleClose = (event?: any) => {
        setOpen(false);
        onClose?.(event);
    };

    const handleClick = () => {
        if (!hasOpened) {
            setHasOpened(true);
        }
        setOpen(prev => !prev);
    };

    const handleClickAway = () => {
        if (trigger === 'click') {
            setOpen(false);
        }
    };

    const resolvedTitle = useMemo(() => {
        if (!lazyMountTitle) {
            return typeof title === 'function' ? title() : title;
        }

        if (!hasOpened) {
            return null;
        }

        return typeof title === 'function' ? title() : title;
    }, [ title, lazyMountTitle, hasOpened ]);

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div>
                <Tooltip
                    {...rest}
                    title={resolvedTitle ?? ''}
                    open={open}
                    onOpen={trigger === 'hover' ? handleOpen : undefined}
                    onClose={trigger === 'hover' ? handleClose : undefined}
                    disableFocusListener={trigger !== 'hover'}
                    disableHoverListener={trigger !== 'hover'}
                    disableTouchListener={trigger !== 'hover'}
                    disableInteractive={trigger !== 'hover'}
                    enterDelay={trigger === 'hover' ? 100 : 0}
                    leaveDelay={trigger === 'hover' ? 120 : 0}
                    slotProps={{
                        ...slotProps,
                        popper: {
                            keepMounted: true,
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [ -24, 0 ],
                                    },
                                },
                            ],
                            ...slotProps?.popper,
                        },
                    }}
                >
                    <div
                        onClick={trigger === 'click' ? handleClick : undefined}
                        style={{ display: 'inline-flex' }}
                    >
                        {children}
                    </div>
                </Tooltip>
            </div>
        </ClickAwayListener>
    );
};

export default CustomTooltip;
